// Global variables for managing state
let abortController = null;
let openDrawers = new Set();

// Global event listener for facet updates
document.addEventListener('facet:update', async (event) => {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  
  const url = event.detail.url;
  const sectionId = url.searchParams.get('section_id');
  // Find the main section element that contains our collection content
  const shopifySection = document.querySelector('[id*="shopify-section-template"][id*="__main"]');
  
  if (!shopifySection) {
    console.error('Could not find section with ID:', sectionId);
    return;
  }
  
  // Update browser URL (remove section_id from visible URL)
  const clonedUrl = new URL(url);
  clonedUrl.searchParams.delete('section_id');
  history.replaceState({}, '', clonedUrl.toString());
  
  try {
    const response = await fetch(url.toString(), { signal: abortController.signal });
    const tempContent = new DOMParser().parseFromString(await response.text(), 'text/html');
    
    // Update collection header (excluding drawer)
    const newCollectionHeader = tempContent.querySelector('[sub-section-id*="collection-header"]');
    const currentCollectionHeader = shopifySection.querySelector('[sub-section-id*="collection-header"]');
    
    if (newCollectionHeader && currentCollectionHeader) {
      // Only update the filters button container, not the drawer
      const newFiltersButtonContainer = newCollectionHeader.querySelector('.filters__button-container');
      const currentFiltersButtonContainer = currentCollectionHeader.querySelector('.filters__button-container');
      
      if (newFiltersButtonContainer && currentFiltersButtonContainer) {
        currentFiltersButtonContainer.outerHTML = newFiltersButtonContainer.outerHTML;
        
        // Rebind drawer trigger events after button replacement
        rebindDrawerTrigger();
      }
    }
    
    // Update collection grid
    const newCollectionGrid = tempContent.querySelector('[sub-section-id*="collection-grid"]');
    const currentCollectionGrid = shopifySection.querySelector('[sub-section-id*="collection-grid"]');
    
    if (newCollectionGrid && currentCollectionGrid) {
      currentCollectionGrid.outerHTML = newCollectionGrid.outerHTML;
    }
    
    // Update active facets (outside the drawer)
    const newActiveFacets = tempContent.querySelector('.filters__active-facets');
    const currentActiveFacets = document.querySelector('.filters__active-facets');
    
    if (newActiveFacets && currentActiveFacets) {
      currentActiveFacets.outerHTML = newActiveFacets.outerHTML;
      // Rebind active facet remove buttons
      rebindActiveFacetButtons();
    } else if (newActiveFacets && !currentActiveFacets) {
      // Add new active facets if they don't exist
      const headerElement = shopifySection.querySelector('[sub-section-id*="collection-header"]');
      if (headerElement) {
        headerElement.insertAdjacentHTML('afterend', newActiveFacets.outerHTML);
        // Rebind active facet remove buttons
        rebindActiveFacetButtons();
      }
    } else if (!newActiveFacets && currentActiveFacets) {
      // Remove active facets if they no longer exist
      currentActiveFacets.remove();
    }
    
    // Hide/show editorial tiles based on active filters
    const hasActiveFilters = [...url.searchParams.keys()].some(k => k.startsWith('filter.'));
    shopifySection.querySelectorAll('.editorial-tile').forEach(tile => {
      if (hasActiveFilters) {
        tile.setAttribute('hidden', '');
      } else {
        tile.removeAttribute('hidden');
      }
    });

    // Update drawer filter options based on new results
    updateDrawerFilterOptions(tempContent);
  
    // Apply view preference after content update
    const currentView = localStorage.getItem('collection_view') || 'product';
    if (currentView === 'model') {
      applyModelView();
    }
  
    // Reinitialize components
    initializeComponents();
    
    // Dispatch the old event for backward compatibility with grid-item.js
    document.dispatchEvent(new CustomEvent('filters:updated', {
      bubbles: true,
      detail: { url: url.toString() }
    }));
    
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error updating facets:', error);

    }
  }
});

// Function to update drawer filter options based on new results
function updateDrawerFilterOptions(newContent) {
  const currentDrawer = document.querySelector('drawer-ui facet-filters');
  const newDrawer = newContent.querySelector('drawer-ui facet-filters');
  
  if (!currentDrawer || !newDrawer) return;
  
  // Get all filter inputs in both current and new drawer
  const currentInputs = currentDrawer.querySelectorAll('input[type="checkbox"]');
  const newInputs = newDrawer.querySelectorAll('input[type="checkbox"]');
  
  // Create a map of new input states by their ID
  const newInputStates = new Map();
  newInputs.forEach(input => {
    const dataCount = input.getAttribute('data-count');
    const count = dataCount ? parseInt(dataCount) : 0;
    newInputStates.set(input.id, {
      dataCount: dataCount,
      disabled: count === 0,
      count: count
    });
  });
  
  // Update current inputs based on new states
  currentInputs.forEach(input => {
    const newState = newInputStates.get(input.id);
    if (newState) {
      // Update data-count attribute
      if (newState.dataCount) {
        input.setAttribute('data-count', newState.dataCount);
      } else {
        input.removeAttribute('data-count');
      }
      
      // Update disabled state
      const label = input.closest('label');
      if (newState.disabled && !input.checked) {
        // Disable if count is 0 and not currently selected
        input.disabled = true;
        if (label) {
          label.classList.add('filter-option--disabled');
          label.style.opacity = '0.2';
          label.style.pointerEvents = 'none';
        }
      } else {
        // Enable if count > 0 or currently selected
        input.disabled = false;
        if (label) {
          label.classList.remove('filter-option--disabled');
          label.style.opacity = '';
          label.style.pointerEvents = '';
        }
      }
    
    }
  });
}

// Function to rebind drawer trigger events after button replacement
function rebindDrawerTrigger() {
  // Find all drawer trigger buttons
  const drawerTriggers = document.querySelectorAll('[data-action="toggle-drawer"]');
  
  drawerTriggers.forEach(trigger => {
    // Get the drawer ID from aria-controls
    const drawerId = trigger.getAttribute('aria-controls');
    const drawer = document.getElementById(drawerId);
    
    if (drawer && drawer.tagName.toLowerCase() === 'drawer-ui') {
      // Remove any existing event listeners by cloning the button
      const newTrigger = trigger.cloneNode(true);
      trigger.parentNode.replaceChild(newTrigger, trigger);
      
      // Add the click event listener to the new button
      newTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        drawer.toggle();
      });
    }
  });
}

// Function to rebind active facet remove and clear all buttons
function rebindActiveFacetButtons() {
  // Handle individual facet remove buttons
  const facetRemoveButtons = document.querySelectorAll('.active-facet__remove a');
  facetRemoveButtons.forEach(button => {
    // Remove existing listeners by cloning
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);
    
    // Add new event listener
    newButton.addEventListener('click', (e) => {
      e.preventDefault();
      const url = new URL(newButton.href);
      url.searchParams.set('section_id', 'collection-product-single-group');
      
      document.dispatchEvent(new CustomEvent('facet:update', {
        bubbles: true,
        detail: { url }
      }));
    });
  });
  
  // Handle clear all button
  const clearAllButtons = document.querySelectorAll('.active-facet__clear-all');
  clearAllButtons.forEach(button => {
    // Remove existing listeners by cloning
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);
    
    // Add new event listener
    newButton.addEventListener('click', (e) => {
      e.preventDefault();
      const url = new URL(newButton.href);
      url.searchParams.set('section_id', 'collection-product-single-group');
      
      document.dispatchEvent(new CustomEvent('facet:update', {
        bubbles: true,
        detail: { url }
      }));
    });
  });
}

class FacetFilters extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('.filters__form');
    this.sortSelect = this.form ? this.form.querySelector('select[name="sort_by"]') : null;
    this.viewRadios = this.form ? this.form.querySelectorAll('input[name="View"]') : [];
    this.bindEvents();
  }

  connectedCallback() {
    this.initializeView();
  }

  bindEvents() {
    // Apply filters immediately on change
    this.form.addEventListener('change', this.onFilterChanged.bind(this));
    
    // Handle sort dropdown changes using Select2 events
    if (this.sortSelect) {
      $(this.sortSelect).on('select2:select', this.onSortChanged.bind(this));
    }
    
    // Handle view radio changes
    if (this.viewRadios.length > 0) {
      this.viewRadios.forEach(radio => {
        radio.addEventListener('change', this.onViewChanged.bind(this));
      });
    }
    
    // Handle clicks on active facet remove buttons
    document.addEventListener('click', this.handleActiveFacetRemove.bind(this));
  }

  initializeView() {
    // Check if there's a saved view preference in localStorage
    const savedView = localStorage.getItem('collection_view');
    
    if (savedView) {
      const viewRadio = this.form.querySelector(`input[name="View"][value="${savedView}"]`);
      if (viewRadio) {
        viewRadio.checked = true;
        this.applyView(savedView);
      }
    } else {
      // Default to product view
      const productRadio = this.form.querySelector('input[name="View"][value="product"]');
      if (productRadio) {
        productRadio.checked = true;
        this.applyView('product');
      }
    }
  }

  onViewChanged(event) {
    const viewValue = event.target.value;
    
    // Save preference to localStorage
    localStorage.setItem('collection_view', viewValue);
    
    // Apply the view
    this.applyView(viewValue);
  }

  applyView(viewValue) {
    // Get all grid items
    const gridItems = document.querySelectorAll('.shop-grid-item-v5');
    
    gridItems.forEach(gridItem => {
      const primaryImage = gridItem.querySelector('.shop-grid-item-primary-image');
      const hoverImage = gridItem.querySelector('.shop-grid-item-hover-image:not(.shop-grid-item-hover-image--hidden)');
      
      // Skip if either image doesn't exist
      if (!primaryImage || !hoverImage) return;
      
      // Check if images have already been swapped (using data attribute to track state)
      const isSwapped = gridItem.dataset.imagesSwapped === 'true';
      
      if (viewValue === 'model' && !isSwapped) {
        // Swap to model view: exchange src attributes
        const primarySrc = primaryImage.src;
        const hoverSrc = hoverImage.src;
        
        primaryImage.src = hoverSrc;
        hoverImage.src = primarySrc;
        
        // Also swap srcset if they exist
        if (primaryImage.srcset && hoverImage.srcset) {
          const primarySrcset = primaryImage.srcset;
          const hoverSrcset = hoverImage.srcset;
          
          primaryImage.srcset = hoverSrcset;
          hoverImage.srcset = primarySrcset;
        }
        
        gridItem.dataset.imagesSwapped = 'true';
      } else if (viewValue === 'product' && isSwapped) {
        // Swap back to product view: exchange src attributes back
        const primarySrc = primaryImage.src;
        const hoverSrc = hoverImage.src;
        
        primaryImage.src = hoverSrc;
        hoverImage.src = primarySrc;
        
        // Also swap srcset back if they exist
        if (primaryImage.srcset && hoverImage.srcset) {
          const primarySrcset = primaryImage.srcset;
          const hoverSrcset = hoverImage.srcset;
          
          primaryImage.srcset = hoverSrcset;
          hoverImage.srcset = primarySrcset;
        }
        
        gridItem.dataset.imagesSwapped = 'false';
      }
    });
    
    // Swap all swatch data attributes across all grid items
    this.swapAllSwatchImages(viewValue);
  }

  swapAllSwatchImages(viewValue) {
    // Loop through all grid items
    const gridItems = document.querySelectorAll('.shop-grid-item-v5');
    
    gridItems.forEach(gridItem => {
      // Find all swatches in this grid item that have a data-image and data-model-image
      const swatches = gridItem.querySelectorAll('[data-image][data-model-image]');
      
      swatches.forEach(swatch => {
        const productImage = swatch.getAttribute('data-product-image');
        const modelImage = swatch.getAttribute('data-model-image');
        
        // Only update if both product and model images exist
        if (productImage && modelImage) {
          if (viewValue === 'model') {
            // Set model image as the primary data-image
            swatch.setAttribute('data-image', modelImage);
            swatch.setAttribute('data-alt-image', productImage);
          } else {
            // Set product image as the primary data-image
            swatch.setAttribute('data-image', productImage);
            swatch.setAttribute('data-alt-image', modelImage);
          }
        }
      });
    });
  }

  onSortChanged(event) {
    // Get the selected sort value from Select2 event data
    const sortValue = event.params.data.id;
    
    // Get current URL and update sort_by parameter
    const url = new URL(window.location.href);
    
    if (sortValue) {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }
    
    // Reset to page 1 when sorting changes
    url.searchParams.delete('page');
    
    // Add section ID and dispatch update
    url.searchParams.set('section_id', 'collection-product-single-group');
    
    document.dispatchEvent(new CustomEvent('facet:update', {
      bubbles: true,
      detail: { url }
    }));
  }

  dispatchFacetUpdate() {
    // Build URL from current form state
    const url = this.buildUrlFromForm();
    
    document.dispatchEvent(new CustomEvent('facet:update', {
      bubbles: true,
      detail: { url }
    }));
  }

  buildUrlFromForm() {
    // Get all checked inputs from the form
    const checkedInputs = this.form.querySelectorAll('input[type="checkbox"]:checked');
    const url = new URL(window.location.href);

    // Clear existing filter params and pagination
    Array.from(url.searchParams.keys()).forEach(key => {
      if (key.startsWith('filter.') || key === 'page') {
        url.searchParams.delete(key);
      }
    });

    // Add filter parameters for checked inputs
    checkedInputs.forEach(input => {
      const filterParam = input.getAttribute('data-filter-param');
      const value = input.value;
      
      if (filterParam && value) {
        url.searchParams.append(filterParam, value);
      }
    });

    // Preserve the current sort_by parameter if it exists
    const currentSortBy = new URL(window.location.href).searchParams.get('sort_by');
    if (currentSortBy) {
      url.searchParams.set('sort_by', currentSortBy);
    }

    // Add section ID
    url.searchParams.set('section_id', 'collection-product-single-group');

    return url;
  }

  handleActiveFacetRemove(event) {
    // Check if clicked element is within an active facet remove link
    const removeLink = event.target.closest('.active-facet__remove a, .active-facet__clear-all');
    
    if (removeLink) {
      event.preventDefault();
      
      const url = new URL(removeLink.href);
      
      // Update the corresponding checkbox in the form
      this.syncFormWithUrl(url);
      
      // Fetch and update content
      this.fetchAndUpdateContent(url);
    }
  }

  syncFormWithUrl(url) {
    // Get all checkboxes
    const checkboxes = this.form.querySelectorAll('input[type="checkbox"]');
    
    // Get filter params from URL
    const urlFilters = new Set();
    for (let [key, value] of url.searchParams.entries()) {
      if (key.startsWith('filter.')) {
        urlFilters.add(`${key}=${value}`);
      }
    }
    
    // Update checkboxes to match URL state
    checkboxes.forEach(checkbox => {
      const filterParam = checkbox.getAttribute('data-filter-param');
      const value = checkbox.value;
      const filterKey = `${filterParam}=${value}`;
      
      // Check if this filter exists in the URL
      checkbox.checked = urlFilters.has(filterKey);
    });

    // Sync sort dropdown with URL
    if (this.sortSelect) {
      const sortValue = url.searchParams.get('sort_by');
      if (sortValue) {
        this.sortSelect.value = sortValue;
      }
    }
  }

  onFilterChanged(event) {
    // Don't handle sort dropdown or view radio changes here (handled separately)
    if (event.target.name === 'sort_by' || event.target.name === 'View') {
      return;
    }

    this.dispatchFacetUpdate();
  }

  async fetchAndUpdateContent(url) {
    try {
      // Get the main section ID from the collection grid element - restricting this to the collection product single group section given the various collection templates.
      const mainSectionId = 'collection-product-single-group';
      
      // Fetch the main section using Section Rendering API
      const searchParams = url.search ? `${url.search}&section_id=${mainSectionId}` : `?section_id=${mainSectionId}`;
      const fetchUrl = `${window.location.pathname}${searchParams}`;      
      const response = await fetch(fetchUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseText = await response.text();
      const sectionHtml = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Update the collection grid using sub-section-id
      const newCollectionGrid = sectionHtml.querySelector(`[sub-section-id="collection-grid"]`);
      const currentCollectionGrid = document.querySelector(`[sub-section-id="collection-grid"]`);
      
      if (newCollectionGrid && currentCollectionGrid) {
        currentCollectionGrid.innerHTML = newCollectionGrid.innerHTML;
      } else {
        console.error('Could not find collection grid sub-sections');
      }
      
      // Update the collection header (filters button, active facets) using sub-section-id
      const newCollectionHeader = sectionHtml.querySelector(`[sub-section-id="collection-header"]`);
      const currentCollectionHeader = document.querySelector(`[sub-section-id="collection-header"]`);
    
      if (newCollectionHeader && currentCollectionHeader) {
        // Only update the filters button container, not the entire header (avoid touching the drawer)
        const newFiltersButtonContainer = newCollectionHeader.querySelector('.filters__button-container');
        const currentFiltersButtonContainer = currentCollectionHeader.querySelector('.filters__button-container');
        
         if (newFiltersButtonContainer && currentFiltersButtonContainer) {
           currentFiltersButtonContainer.innerHTML = newFiltersButtonContainer.innerHTML;
           
           // Re-bind drawer trigger after button update
           this.rebindDrawerTrigger();
         } else {
           console.error('Could not find filters button container to update');
         }
         
         // Only update drawer filters if this wasn't triggered by a sort change. Sort changes don't affect filter availability, so no need to update the drawer
         if (!this.isCurrentlySorting) {
           this.updateDrawerFilters(newCollectionHeader);
         }
      }
      
      // Update active facets wrapper (outside the header)
      // this.updateActiveFacetsFromHtml(sectionHtml);

      // Only apply view for filter operations, not sort operations
      if (!this.isCurrentlySorting) {
        const currentView = localStorage.getItem('collection_view') || 'product';
        this.applyView(currentView);
      }

      // Update URL
      window.history.pushState({ path: url.toString() }, '', url.toString());

      // Dispatch event
      document.dispatchEvent(new CustomEvent('filters:updated', { 
        detail: { url: url.toString() }
      }));

    } catch (error) {
      console.error('Error updating filters:', error);
      window.location.href = url.toString();
    } finally {
      // Clear the sorting flag
      this.isCurrentlySorting = false;
    }
  }

  // updateActiveFacetsFromHtml(html) {
  //   try {
  //     // Update the separate active facets wrapper element (outside the collection header)
  //     const newActiveFacetsWrapper = html.querySelector('.filters__active-facets');
  //     const currentActiveFacetsWrapper = document.querySelector('.filters__active-facets');
      
  //     if (newActiveFacetsWrapper && currentActiveFacetsWrapper) {
  //       // Replace the active facets wrapper content
  //       currentActiveFacetsWrapper.outerHTML = newActiveFacetsWrapper.outerHTML;
  //     } else if (!newActiveFacetsWrapper && currentActiveFacetsWrapper) {
  //       // Remove active facets wrapper if there are none in the new content
  //       currentActiveFacetsWrapper.remove();
  //     } else if (newActiveFacetsWrapper && !currentActiveFacetsWrapper) {
  //       // Add active facets wrapper if they don't exist but are in the new content
  //       // Insert after the collection header div (at the same DOM level)
  //       const collectionHeader = document.querySelector('.collection-header');
  //       if (collectionHeader) {
  //         // Insert after the collection-header element
  //         collectionHeader.insertAdjacentElement('afterend', newActiveFacetsWrapper);
  //       }
  //     }
      
  //   } catch (error) {
  //     console.error('Error updating active facets:', error);
  //   }
  // }


  updateDrawerFilters(newCollectionHeader) {
    try {
      // Find the new facet-filters content from the updated header
      const newFacetFilters = newCollectionHeader.querySelector('facet-filters');
      const currentDrawer = document.querySelector('drawer-ui');
      
      if (newFacetFilters && currentDrawer) {
        const currentFacetFilters = currentDrawer.querySelector('facet-filters');
        
        if (currentFacetFilters) {
          // Store current form state (checked inputs, select values)
          const currentFormData = this.captureFormState(currentFacetFilters);
          
          // Use smooth update to minimize flicker
          this.smoothUpdateFacetFilters(currentFacetFilters, newFacetFilters, currentFormData);
          
          // Reinitialize Select2 dropdowns in the updated drawer
          this.reinitializeDrawerSelects();
        }
      }
    } catch (error) {
      console.error('Error updating drawer filters:', error);
    }
  }

  smoothUpdateFacetFilters(currentElement, newElement, formData) {
    try {
      // Create a temporary container to hold the new content
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = newElement.innerHTML;
      
      // Check if there are actually filter changes that need visual feedback
      const hasFilterChanges = this.hasFilterSectionChanges(currentElement, tempContainer);
      
      if (hasFilterChanges) {
        // Only apply opacity transition if there are actual filter changes
        currentElement.style.transition = 'opacity 0.15s ease';
        currentElement.style.opacity = '0.7';
        
        // Use requestAnimationFrame to ensure smooth visual transition
        requestAnimationFrame(() => {
          // Update only the parts that have changed to minimize DOM manipulation
          this.updateFilterSections(currentElement, tempContainer);
          
          // Restore opacity after a brief delay
          requestAnimationFrame(() => {
            currentElement.style.opacity = '1';
            // Remove transition after animation completes
            setTimeout(() => {
              currentElement.style.transition = '';
            }, 150);
          });
        });
      } else {
        // No filter changes, just update silently without visual effects
        this.updateFilterSections(currentElement, tempContainer);
      }
    } catch (error) {
      console.error('Error in smooth update:', error);
      // Fallback to direct innerHTML replacement
      currentElement.innerHTML = newElement.innerHTML;
      this.restoreFormState(currentElement, formData);
    }
  }

  hasFilterSectionChanges(currentElement, newElement) {
    // Check if any actual filter sections (not sort/view) have changes
    const currentSections = currentElement.querySelectorAll('.filters__section[data-filter-section]');
    
    for (const currentSection of currentSections) {
      const filterSectionType = currentSection.getAttribute('data-filter-section');
      
      // Skip checking sort and view sections
      if (filterSectionType === 'Model Viewer' || filterSectionType === 'Sort By') {
        continue;
      }
      
      // Find the matching new section
      const newSection = newElement.querySelector(`[data-filter-section="${filterSectionType}"]`);
      
      if (newSection && currentSection.innerHTML !== newSection.innerHTML) {
        return true; // Found a change in an actual filter section
      }
    }
    
    return false; // No filter section changes
  }

  updateFilterSections(currentElement, newElement) {
    // Get all current sections and match them by data-filter-section attribute
    const currentSections = currentElement.querySelectorAll('.filters__section[data-filter-section]');
    
    currentSections.forEach((currentSection) => {
      const filterSectionType = currentSection.getAttribute('data-filter-section');
      
      // Skip updating Model Viewer and Sort By sections as these are user preferences
      if (filterSectionType === 'Model Viewer' || filterSectionType === 'Sort By') {
        return; // Don't update view toggles or sort dropdown
      }
      
      // Find the matching new section by data-filter-section attribute
      const newSection = newElement.querySelector(`[data-filter-section="${filterSectionType}"]`);
      
      if (newSection) {
        // Only update actual filter sections (Rise, Size, Color, etc.)
        if (currentSection.innerHTML !== newSection.innerHTML) {
          // Update options within the section to preserve structure
          const currentOptions = currentSection.querySelector('.filters__options');
          const newOptions = newSection.querySelector('.filters__options');
          
          if (currentOptions && newOptions && currentOptions.innerHTML !== newOptions.innerHTML) {
            currentOptions.innerHTML = newOptions.innerHTML;
          }
        }
      }
    });
  }

  captureFormState(facetFiltersElement) {
    const formData = {
      checkboxes: {},
      selects: {},
      radios: {}
    };
    
    // Only capture filter checkbox states (not view radios)
    const checkboxes = facetFiltersElement.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      if (checkbox.id && checkbox.getAttribute('data-filter-param')) {
        // Only capture actual filter checkboxes, not other inputs
        formData.checkboxes[checkbox.id] = checkbox.checked;
      }
    });
    
    // Don't capture sort select or view radios - these are user preferences
    // Only capture filter-related selects if any exist
    const selects = facetFiltersElement.querySelectorAll('select');
    selects.forEach(select => {
      if (select.name !== 'sort_by' && (select.id || select.name)) {
        // Skip sort dropdown, only capture filter-related selects
        const key = select.id || select.name;
        formData.selects[key] = select.value;
      }
    });
    
    // Don't capture view radio buttons - these are user preferences
    // Only capture filter-related radio buttons if any exist
    const radios = facetFiltersElement.querySelectorAll('input[type="radio"]');
    radios.forEach(radio => {
      if (radio.name !== 'View' && radio.id && radio.checked && radio.getAttribute('data-filter-param')) {
        // Skip view radios, only capture filter-related radios
        formData.radios[radio.id] = true;
      }
    });
    
    return formData;
  }

  restoreFormState(facetFiltersElement, formData) {
    // Restore filter checkbox states (skip view radios and sort)
    Object.keys(formData.checkboxes || {}).forEach(id => {
      const element = facetFiltersElement.querySelector(`#${id}`);
      if (element && element.getAttribute('data-filter-param')) {
        // Only restore actual filter checkboxes
        element.checked = formData.checkboxes[id];
      }
    });
    
    // Restore filter-related select values (skip sort dropdown)
    Object.keys(formData.selects || {}).forEach(key => {
      const element = facetFiltersElement.querySelector(`#${key}, [name="${key}"]`);
      if (element && element.name !== 'sort_by') {
        // Skip sort dropdown restoration
        element.value = formData.selects[key];
      }
    });
    
    // Restore filter-related radio states (skip view radios)
    Object.keys(formData.radios || {}).forEach(id => {
      const element = facetFiltersElement.querySelector(`#${id}`);
      if (element && element.name !== 'View' && element.getAttribute('data-filter-param')) {
        // Only restore filter-related radios, not view radios
        element.checked = formData.radios[id];
      }
    });
  }

  reinitializeFacetFilters(facetFiltersElement) {
    try {     
      // Re-bind the form and elements to this instance
      this.form = facetFiltersElement.querySelector('.filters__form');
      this.sortSelect = this.form ? this.form.querySelector('select[name="sort_by"]') : null;
      this.viewRadios = this.form ? this.form.querySelectorAll('input[name="View"]') : [];
      
      // Remove old event listeners and rebind new ones
      if (this.form) {
        // Remove existing listeners by cloning the form (removes all listeners)
        const newForm = this.form.cloneNode(true);
        this.form.parentNode.replaceChild(newForm, this.form);
        this.form = newForm;
        
        // Re-bind events to the new form
        this.form.addEventListener('change', this.onFilterChanged.bind(this));
        
        // Re-bind sort dropdown events
        if (this.sortSelect) {
          $(this.sortSelect).on('select2:select', this.onSortChanged.bind(this));
        }
        
        // Re-bind view radio events
        if (this.viewRadios.length > 0) {
          this.viewRadios.forEach(radio => {
            radio.addEventListener('change', this.onViewChanged.bind(this));
          });
        }
      }
    } catch (error) {
      console.error('Error reinitializing FacetFilters:', error);
    }
  }

  rebindDrawerTrigger() {
    // Find the drawer element
    const drawer = document.querySelector('drawer-ui');
    
    if (drawer) {      
      const drawerId = drawer.getAttribute('id');
      const newTrigger = document.querySelector(`[aria-controls="${drawerId}"]`);
      
      if (newTrigger) {        
        // Remove any existing click listeners from the drawer's trigger property
        if (drawer.trigger) {
          drawer.trigger.removeEventListener('click', drawer.toggle.bind(drawer));
        }
        
        // Update the drawer's trigger reference
        drawer.trigger = newTrigger;
        
        // Add the click listener to the new trigger
        newTrigger.addEventListener('click', drawer.toggle.bind(drawer));
        
        // Ensure data-action is set for the event delegation as backup
        if (!newTrigger.getAttribute('data-action')) {
          newTrigger.setAttribute('data-action', 'toggle-drawer');
        }
      } 
    }
  }

  reinitializeDrawerSelects() {
    // Reinitialize Select2 for any custom selects inside drawers
    const drawerSelects = document.querySelectorAll('drawer-ui .custom-select');
    
    drawerSelects.forEach(select => {
      const $select = $(select);
      
      // Destroy existing Select2 instance if it exists
      if ($select.hasClass('select2-hidden-accessible')) {
        $select.select2('destroy');
      }
      
      // Reinitialize with drawer as dropdownParent
      const config = {
        minimumResultsForSearch: Infinity,
        dropdownParent: $select.closest('drawer-ui')
      };
      
      $select.select2(config);
      
      // Rebind Select2 event listeners for sort select
      if (select.name === 'sort_by') {
        $select.on('select2:select', this.onSortChanged.bind(this));
      }
    });
  }
}

// Global helper functions
function applyModelView() {
  const gridItems = document.querySelectorAll('.shop-grid-item-v5');
  
  gridItems.forEach(gridItem => {
    const primaryImage = gridItem.querySelector('.shop-grid-item-primary-image');
    const hoverImage = gridItem.querySelector('.shop-grid-item-hover-image:not(.shop-grid-item-hover-image--hidden)');
    
    if (!primaryImage || !hoverImage) return;
    
    // Check if already swapped
    const isSwapped = gridItem.dataset.imagesSwapped === 'true';
    
    if (!isSwapped) {
      // Swap to model view
      const primarySrc = primaryImage.src;
      const hoverSrc = hoverImage.src;
      
      primaryImage.src = hoverSrc;
      hoverImage.src = primarySrc;
      
      // Also swap srcset if they exist
      if (primaryImage.srcset && hoverImage.srcset) {
        const primarySrcset = primaryImage.srcset;
        const hoverSrcset = hoverImage.srcset;
        
        primaryImage.srcset = hoverSrcset;
        hoverImage.srcset = primarySrcset;
      }
      
      gridItem.dataset.imagesSwapped = 'true';
    }
  });
  
  // Also swap swatch images
  swapAllSwatchImages('model');
}

function swapAllSwatchImages(viewValue) {
  const gridItems = document.querySelectorAll('.shop-grid-item-v5');
  
  gridItems.forEach(gridItem => {
    const swatches = gridItem.querySelectorAll('[data-image][data-model-image]');
    
    swatches.forEach(swatch => {
      const productImage = swatch.getAttribute('data-product-image');
      const modelImage = swatch.getAttribute('data-model-image');
      
      if (productImage && modelImage) {
        if (viewValue === 'model') {
          swatch.setAttribute('data-image', modelImage);
          swatch.setAttribute('data-alt-image', productImage);
        } else {
          swatch.setAttribute('data-image', productImage);
          swatch.setAttribute('data-alt-image', modelImage);
        }
      }
    });
  });
}

function initializeComponents() {
  // Track drawer states
  document.querySelectorAll('drawer-ui').forEach(drawer => {
    if (drawer.classList.contains('drawer--open')) {
      openDrawers.add(drawer.id);
    }
    
    // Re-bind drawer events
    drawer.addEventListener('drawer:opened', () => openDrawers.add(drawer.id));
    drawer.addEventListener('drawer:closed', () => openDrawers.delete(drawer.id));
  });
  
  // Reinitialize Select2 dropdowns
  if (typeof $ !== 'undefined') {
    $('.custom-select').each(function() {
      const $select = $(this);
      const isInDrawer = $select.closest('drawer-ui').length > 0;
      
      // Destroy existing instance
      if ($select.hasClass('select2-hidden-accessible')) {
        $select.select2('destroy');
      }
      
      const config = {
        minimumResultsForSearch: Infinity,
      };
      
      if (isInDrawer) {
        config.dropdownParent = $select.closest('drawer-ui');
      }
      
      $select.select2(config);
    });
  }
}

if (!customElements.get('facet-filters')) {
  customElements.define('facet-filters', FacetFilters);
}

// Bind active facet buttons on initial load
document.addEventListener('DOMContentLoaded', () => {
  rebindActiveFacetButtons();
});